import { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import {
  BookOpen, CheckCircle2, Loader2,
  FlaskConical, Code, Wrench, Calculator, Cpu, Search, Sparkles,
} from 'lucide-react';
import axios from 'axios';
import { getThemeVisuals } from '../data/divisionThemeVisuals';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ───── Category → icon + gradient styling (default fallback) ───── */
const CATEGORY_STYLE = {
  science:     { icon: FlaskConical, gradient: 'from-rose-500 to-pink-400',     accent: 'text-rose-600',    bg: 'bg-rose-50',    ring: 'ring-rose-200' },
  technology:  { icon: Code,         gradient: 'from-emerald-500 to-teal-400',  accent: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
  coding:      { icon: Code,         gradient: 'from-emerald-500 to-teal-400',  accent: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
  engineering: { icon: Wrench,       gradient: 'from-amber-500 to-orange-400',  accent: 'text-amber-600',   bg: 'bg-amber-50',   ring: 'ring-amber-200' },
  math:        { icon: Calculator,   gradient: 'from-sky-500 to-blue-400',      accent: 'text-sky-600',     bg: 'bg-sky-50',     ring: 'ring-sky-200' },
  robotics:    { icon: Cpu,          gradient: 'from-purple-500 to-violet-400', accent: 'text-purple-600',  bg: 'bg-purple-50',  ring: 'ring-purple-200' },
  default:     { icon: Sparkles,     gradient: 'from-rsc-navy to-slate-700',    accent: 'text-rsc-navy',    bg: 'bg-gray-50',    ring: 'ring-gray-200' },
};

/**
 * Return modal styling for a course — prefers the per-title override from the
 * shared visual registry (so the modal matches the division-page card colour)
 * and falls back to the category default.
 */
function getStyle(course) {
  const v = getThemeVisuals(course.title, course.category);
  if (v && v.icon) {
    // Map the division-page accent classes onto dark-background-friendly equivalents.
    const baseColor = (v.colorAccent || '').replace(/text-([a-z]+)-\d+/, '$1');
    return {
      icon: v.icon,
      gradient: v.color,
      accent: baseColor ? `text-${baseColor}-600` : CATEGORY_STYLE.default.accent,
      bg: baseColor ? `bg-${baseColor}-50` : CATEGORY_STYLE.default.bg,
      ring: baseColor ? `ring-${baseColor}-200` : CATEGORY_STYLE.default.ring,
    };
  }
  return CATEGORY_STYLE[(course.category || '').toLowerCase()] || CATEGORY_STYLE.default;
}

function tagline(course) {
  const parts = [];
  if (course.subcategory === 'summer_camp') parts.push('Summer camp');
  else if (course.subcategory === 'weekend_camp') parts.push('Weekend camp');
  else if (course.category) parts.push(`${course.category[0].toUpperCase()}${course.category.slice(1)} course`);
  const gb = (course.grade_bands || []).join(', ').replace(/-/g, '–');
  if (gb) parts.push(`Grades ${gb}`);
  return parts.join(' · ');
}

export function useCourseDetailModal() {
  const [state, setState] = useState({ open: false, course: null, loading: false });

  const openCourseDetail = async (courseIdOrTitle) => {
    if (!courseIdOrTitle) return;
    setState({ open: true, course: null, loading: true });
    try {
      let courseData;
      const isUUID = /^[0-9a-f]{8}-/.test(courseIdOrTitle);
      if (isUUID) {
        const res = await axios.get(`${API_URL}/api/courses/${courseIdOrTitle}/`);
        courseData = res.data;
      } else {
        const res = await axios.get(`${API_URL}/api/courses/?search=${encodeURIComponent(courseIdOrTitle)}&page_size=5`);
        const courses = res.data?.results || res.data || [];
        const match = courses.find(c => c.title === courseIdOrTitle) || courses[0];
        if (!match) { setState({ open: false, course: null, loading: false }); return; }
        // The list endpoint returns a SUMMARY without sample_activities / kit_items.
        // Fetch the detail endpoint so the rich modal body (Activities + Kit) populates
        // the same way it does when opened via UUID on the summer-camps page.
        try {
          const detailRes = await axios.get(`${API_URL}/api/courses/${match.id}/`);
          courseData = detailRes.data;
        } catch {
          courseData = match;
        }
      }
      const lessonsRes = await axios.get(`${API_URL}/api/courses/${courseData.id}/lessons/`).catch(() => ({ data: [] }));
      setState({ open: true, course: { ...courseData, lessons: lessonsRes.data || [] }, loading: false });
    } catch {
      setState({ open: false, course: null, loading: false });
    }
  };

  const close = () => setState({ open: false, course: null, loading: false });

  return { state, openCourseDetail, close };
}

export function CourseDetailModal({ state, onClose, onRegister }) {
  const { open, course, loading } = state;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto p-0"
        data-testid="course-detail-modal"
      >
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-rsc-yellow" />
          </div>
        ) : course && (() => {
          const s = getStyle(course);
          const Icon = s.icon;
          const topics = course.topic_tags || [];
          const activities = course.sample_activities || [];
          const kitItems = course.kit_items || (course.kit_details?.components || []);
          const lessons = course.lessons || [];
          const outcomes = course.learning_outcomes || [];

          return (
            <>
              {/* ───── Hero: image (if uploaded) or gradient header with icon + title + topic pills ───── */}
              <div className="relative overflow-hidden">
                {course.thumbnail_url ? (
                  <>
                    <img
                      src={course.thumbnail_url}
                      alt={course.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${s.gradient} opacity-70 mix-blend-multiply`} />
                  </>
                ) : (
                  <div className={`absolute inset-0 bg-gradient-to-br ${s.gradient} opacity-95`} />
                )}
                <div className="relative px-6 py-6 text-white">
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/30`}>
                      <Icon className="w-8 h-8 text-white" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl sm:text-2xl font-heading font-bold text-white leading-tight mb-1" data-testid="course-detail-title">
                        {course.title}
                      </h2>
                      <p className="text-sm text-white/90">
                        {tagline(course)}
                      </p>
                    </div>
                  </div>

                  {topics.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4" data-testid="course-detail-topics">
                      {topics.map((t, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-white/20 backdrop-blur-sm text-white ring-1 ring-white/30"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ───── Body ───── */}
              <div className="p-6 space-y-4">
                {course.description && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4" data-testid="course-detail-description">
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      {course.description}
                    </p>
                  </div>
                )}

                {/* ───── Two-column: Activities / Kit ───── */}
                {(activities.length > 0 || kitItems.length > 0) && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {activities.length > 0 && (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <h4 className={`text-[11px] font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${s.accent}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Sample Activities
                        </h4>
                        <ul className="space-y-2" data-testid="course-detail-activities">
                          {activities.map((a, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                              <span>{a}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {kitItems.length > 0 && (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <h4 className={`text-[11px] font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${s.accent}`}>
                          <Search className="w-3.5 h-3.5" />
                          What's in the Kit
                        </h4>
                        <ul className="space-y-2" data-testid="course-detail-kit-items">
                          {kitItems.map((k, i) => {
                            const label = typeof k === 'string' ? k : (k?.name || k?.label || '');
                            return (
                              <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                                <span className={`inline-block w-3 h-3 rounded-full bg-gradient-to-br ${s.gradient} flex-shrink-0 mt-0.5`} />
                                <span>{label}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* ───── Optional: Learning Outcomes ───── */}
                {outcomes.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <h4 className={`text-[11px] font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${s.accent}`}>
                      <Sparkles className="w-3.5 h-3.5" />
                      What Your Child Will Learn
                    </h4>
                    <ul className="space-y-1.5">
                      {outcomes.map((o, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className={`${s.accent} font-bold mt-0.5`}>•</span> {o}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* ───── Optional: Session Outline (for non-camp courses with lesson plans) ───── */}
                {lessons.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <h4 className="text-[11px] font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5 text-blue-600">
                      <BookOpen className="w-3.5 h-3.5" />
                      Session Outline ({lessons.length} sessions)
                    </h4>
                    <div className="space-y-2">
                      {lessons.map(lesson => (
                        <div key={lesson.id} className="border-l-2 border-blue-200 pl-3">
                          <p className="text-sm font-semibold text-rsc-navy">
                            <span className="text-blue-600 mr-1">{lesson.lesson_number}.</span>
                            {lesson.title}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ───── Footer ───── */}
                <div className="pt-2 border-t border-gray-200">
                  <Button
                    variant="outline"
                    className="w-full border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-600"
                    onClick={onClose}
                    data-testid="course-detail-close-btn"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}
